import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CommonModule } from '@angular/common';
import { ExpertProfileHeaderComponent } from '../../components/expert-profile-header/expert-profile-header';
import { ExpertProfileAboutComponent } from '../../components/expert-profile-about/expert-profile-about';
import { ExpertProfilePropertiesComponent } from '../../components/expert-profile-properties/expert-profile-properties';
import { ExpertProfileDealsComponent } from '../../components/expert-profile-deals/expert-profile-deals';
import { ExpertProfileReviewsComponent } from '../../components/expert-profile-reviews/expert-profile-reviews';
import { ExpertProfileContactComponent } from '../../components/expert-profile-contact/expert-profile-contact';
import { ExpertsService } from '../../services/experts.service';
import { Expert } from '../../components/expert-card/expert-card';
import { TranslatePipe } from '../../../../core/pipes/translate.pipe';

@Component({
  selector: 'app-experts-profile',
  imports: [
    CommonModule,
    ExpertProfileHeaderComponent,
    ExpertProfileAboutComponent,
    ExpertProfilePropertiesComponent,
    ExpertProfileDealsComponent,
    ExpertProfileReviewsComponent,
    ExpertProfileContactComponent,
    TranslatePipe,
  ],
  templateUrl: './experts-profile.html',
  styleUrl: './experts-profile.scss',
})
export class ExpertsProfile implements OnInit {
  expert: Expert | null = null;
  isLoading = false;

  constructor(
    private route: ActivatedRoute,
    private expertsService: ExpertsService,
  ) {}

  ngOnInit(): void {
    const state = history.state;
    this.route.paramMap.subscribe((params) => {
      const id = params.get('id');
      if (id) {
        if (state && state.profileUrl) {
          this.loadExpertProfileFromUrl(state.profileUrl, id);
        } else {
          this.loadExpertProfile(id);
        }
      }
    });
  }

  loadExpertProfileFromUrl(profileUrl: string, id: string): void {
    this.isLoading = true;
    this.expertsService.getExpertByProfileUrl(profileUrl).subscribe({
      next: (response) => {
        console.log('📡 Expert Profile Details Response (from profileUrl):', response);
        const raw = response?.resProfile || response?.data || response;
        this.expert = this.mapApiExpertToFrontend(raw);
        this.isLoading = false;
      },
      error: (err) => {
        console.warn('⚠️ Direct expert fetch by profileUrl failed, attempting fallback by ID:', err);
        this.loadExpertProfile(id);
      },
    });
  }

  loadExpertProfile(id: string): void {
    this.isLoading = true;
    this.expertsService.getExpertById(id).subscribe({
      next: (response) => {
        console.log('📡 Expert Profile Details Response (from ID):', response);
        const raw = response?.resProfile || response?.data || response;
        this.expert = this.mapApiExpertToFrontend(raw);
        this.isLoading = false;
      },
      error: (err) => {
        console.warn('⚠️ Direct expert fetch by ID failed, attempting fallback list search:', err);
        this.fallbackFetchFromList(id);
      },
    });
  }

  private fallbackFetchFromList(id: string): void {
    this.expertsService.getExperts({ limit: 50 }).subscribe({
      next: (response) => {
        const rawExperts = response?.data || [];
        const match = rawExperts.find((x: any) => x._id === id || x.id === id || x.userId === id);
        if (match) {
          this.expert = this.mapApiExpertToFrontend(match);
        } else {
          console.error(`❌ Expert ID ${id} not found in fallback search.`);
        }
        this.isLoading = false;
      },
      error: (err) => {
        console.error('❌ Fallback list fetch failed:', err);
        this.isLoading = false;
      },
    });
  }

  private mapApiExpertToFrontend(item: any): Expert {
    // Parse yearsOfExperience
    let experienceYears = 0;
    if (item.yearsOfExperience) {
      const parsed = parseInt(item.yearsOfExperience, 10);
      if (!isNaN(parsed)) {
        if (parsed > 1900) {
          experienceYears = new Date().getFullYear() - parsed;
        } else {
          experienceYears = parsed;
        }
      }
    }

    // Keep labels translation-driven so the profile follows the active language.
    let typeName = 'experts.types.broker';
    if (item.type === 'developer') {
      typeName = 'experts.types.developer';
    } else if (item.type === 'brokerage-company') {
      typeName = 'experts.types.broker_company';
    }

    // Build location string from locations array
    let locationStr = 'experts.common.notSpecified';
    if (Array.isArray(item.locations) && item.locations.length > 0) {
      locationStr = item.locations.join(', ');
    }

    // Premium color palette matching HomiGo theme
    const premiumBgColors = ['451a03', '7c2d12', '9a3412', 'ea580c', 'de5806'];
    const randomBg = premiumBgColors[Math.floor(Math.random() * premiumBgColors.length)];

    // Parse about object (ar/en)
    let parsedAbout = undefined;
    if (item.about && typeof item.about === 'object') {
      parsedAbout = item.about.ar || item.about.en || undefined;
    } else if (typeof item.about === 'string') {
      parsedAbout = item.about;
    }

    return {
      id: item._id || item.id,
      name: item.name || 'Expert',
      image: item.image?.secure_url || item.profileImage || item.logo || `https://ui-avatars.com/api/?name=${encodeURIComponent(item.name || 'Expert')}&background=${randomBg}&color=fff&size=150`,
      type: item.type || 'broker',
      typeName: typeName,
      rating: item.averageRating || 0,
      reviewsCount: item.totalReviews || 0,
      experienceYears: experienceYears,
      propertiesCount: item.dealsOrProjectsCount || item.propertiesCount || 0,
      dealsCount: item.dealsOrProjectsCount || item.successfulDeals || 0,
      ratingScore: item.averageRating || 0,
      tags: Array.isArray(item.tags) ? item.tags : [],
      location: locationStr,
      expertType: item.expertType || 'broker',
      typeNameKey: `experts.types.${item.type === 'developer' ? 'developer' : item.expertType === 'broker_company' || item.brokerType === 'company' ? 'broker_company' : 'broker'}`,
      brokerType: item.brokerType,
      serviceAreas: item.serviceAreas || [],
      languages: item.languages || [],
      isVerified: !!item.isTrusted,
      about: parsedAbout,
      aboutCompany: item.aboutCompany,
      socialLinks: item.socialLinks,
      specializations: item.specializations ? (Array.isArray(item.specializations) ? item.specializations : [item.specializations]) : [],
      commercialRegisterNumber: item.commercialRegisterNumber,
      certificatesAndLicenses: item.certificatesAndLicenses || [],
      achievementsAndAwards: item.achievementsAndAwards || [],
      phoneNumber: item.phoneNumber,
      email: item.email,
      website: item.website,
      avgResponseTimeMinutes: item.avgResponseTimeMinutes,
      responseTimeLabel: item.responseTimeLabel,
    };
  }
}


